import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import FinancialPlanHighlightsSection from './components/financialPlanHighlightsSection';
import HeroSection from './components/heroSection'; // Coming back soon
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import SectionVideoPlayer from './components/sectionVideoPlayer';
import SuspectEmailCaptureFormSection from './components/suspectEmailCaptureFormSection';
import UnderstandingProduct from './components/understandingProduct';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/financial-planning-hero.webp';
import highResMobileImage from './components/heroSection/images/financial-planning-hero-mobile.webp';
// dislaimer footnotes
import {
  HighestFinancialStrengthRatings,
  RankingNmInvestmentsServices,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Hal E. Hershfield,{' '}
          <Link
            variant='primaryDeemphasize'
            linkUrl='/assets/pdf/assessing-the-value-of-a-holistic-advisor.pdf'
            className='nmx-link'
            isInternalPdf>
            Assessing the Value of a Holistic Advisor (2024)
          </Link>
          . In Hal Hershfield&#39;s research, a comprehensive approach to planning means a client has Permanent Life Insurance, either investments or annuities, and a recent financial plan.
        </>,
        <>
          {HighestFinancialStrengthRatings(2)}
        </>,
        <>
          {RankingNmInvestmentsServices(3)}
        </>,
        <>
          <sup>4</sup>&nbsp;Dividends are reviewed annually by the company and are not guaranteed. Some policies may not receive dividends in a particular year or years even while other policies receive dividends. For universal life products, in lieu of dividends, experience is reflected through changes to nonguaranteed charges and credits.
        </>,
      ]}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Financial Planning',
          linkUrl: '/financial-planning/',
          linkAriaLabel: 'Northwestern Mutual Financial Planning',
        },
      ]}>
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      {/* INTRO */}
      <FinancialPlanHighlightsSection />

      <UnderstandingProduct />

      <ProductCalloutGrouping />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='financial-planning'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <Faq />

      {/* Hide for users with an advisor */}
      <LazyLoad
        offset={300}
        height={300}
        once
        placeholder={
          <section id='financial-planning-page-suspect-email-capture-form'>
            <h2>Get weekly emails to help you reach your goals, big or small.</h2>
          </section>
        }>
        <SuspectEmailCaptureFormSection
          config={config}
          loadRecaptcha={loadRecaptcha}
          setLoadRecaptcha={setLoadRecaptcha}
        />
      </LazyLoad>

      <SectionVideoPlayer />

      <CredentialCalloutGrouping />

      <AdvisorConnectModule />

      <ContentHub
        sectionId='content-hub'
        backgroundVariant='lightC'
        headingText='See what financial planning can do for you'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

    </Page>
  );
};
